/**
 * A small slippy map, for choosing a point and nothing else.
 *
 * Two deliberate decisions are worth stating, because both look like under-engineering:
 *
 * **It is hand-written.** MapLibre and Leaflet are excellent and both are an order of magnitude
 * more code than the entire rest of this app. What is needed here is a grid of `<img>` tiles that
 * pans and zooms — about two hundred lines — and every dependency in this repo has to justify
 * itself against exactly that comparison.
 *
 * **The crosshair does not move.** The pin is always dead centre and the *world* moves under it.
 * Tapping to place a pin means your thumb covers the thing you are placing, and dragging a pin
 * on a phone means fighting the map for which one you meant to grab. Moving the map under a
 * fixed crosshair has neither problem and works one-handed.
 *
 * Tiles come from this app's own origin (`/api/tiles/...`), which proxies and caches them. The
 * browser never talks to a tile server, so the "no third-party requests" rule survives.
 */

import {
  TILE_SIZE,
  clampLatitude,
  metresPerPixel,
  project,
  unproject,
  wrapTileX,
  type LatLng,
} from '@beeline/shared';

import { el } from '../lib/dom';

export interface MapViewOptions {
  centre: LatLng;
  zoom?: number;
  /** Layers the server actually has. Empty means the map cannot be shown at all. */
  layers: string[];
  maxZoom: number;
  /** Fired whenever the crosshair ends up somewhere new. */
  onMove?: (centre: LatLng, zoom: number) => void;
  /**
   * Where the device actually is, drawn as a translucent disc sized by its accuracy — so "your
   * position" is never a dot pretending to be exact.
   *
   * It is a *position*, not just a radius, because the crosshair moves and the user does not.
   * Pinning the disc to the centre of the screen would make it follow whatever point is being
   * chosen, which is the one thing it must never claim.
   */
  you?: LatLng | null;
  accuracyMetres?: number | null;
}

export interface MapView {
  element: HTMLElement;
  /** Where the crosshair is: the point being chosen. */
  centre: () => LatLng;
  zoom: () => number;
  panTo: (position: LatLng, zoom?: number) => void;
  /** Redraw the "you are here" disc, e.g. when a better fix lands. */
  setYou: (position: LatLng | null, accuracyMetres: number | null) => void;
  destroy: () => void;
}

const MIN_ZOOM = 2;
/** Close enough to pick out a parked car; further in, the imagery is invented anyway. */
const DEFAULT_ZOOM = 17;

/** How far a pointer may travel before a press counts as a drag rather than a tap. */
const DRAG_SLOP_PX = 6;

const clamp = (value: number, low: number, high: number): number =>
  Math.min(high, Math.max(low, value));

export function createMapView(options: MapViewOptions): MapView {
  const maxZoom = Math.max(MIN_ZOOM, options.maxZoom);
  const layer = options.layers[0] ?? 'map';

  let zoom = clamp(Math.round(options.zoom ?? DEFAULT_ZOOM), MIN_ZOOM, maxZoom);
  let centre: LatLng = { lat: clampLatitude(options.centre.lat), lng: options.centre.lng };
  let accuracyMetres = options.accuracyMetres ?? null;
  let you: LatLng | null = options.you ?? null;

  /* --- the furniture ----------------------------------------------------- */

  const tileLayer = el('div', { class: 'map__tiles' });
  const accuracyDisc = el('div', { class: 'map__accuracy', hidden: true });
  const crosshair = el(
    'div',
    { class: 'map__crosshair', 'aria-hidden': 'true' },
    el('span', { class: 'map__crosshair-ring' }),
    el('span', { class: 'map__crosshair-stem' }),
  );

  const zoomIn = el('button', { class: 'map__zoom', type: 'button', 'aria-label': 'Zoom in' }, '+');
  const zoomOut = el(
    'button',
    { class: 'map__zoom', type: 'button', 'aria-label': 'Zoom out' },
    '−',
  );

  const element = el(
    'div',
    { class: 'map', role: 'application', 'aria-label': 'Choose a point on the map' },
    tileLayer,
    accuracyDisc,
    crosshair,
    el('div', { class: 'map__zooms' }, zoomIn, zoomOut),
    // Required by every free tile source there is, and the right thing regardless.
    el('p', { class: 'map__attribution', text: '© OpenStreetMap contributors' }),
  );

  /* --- rendering --------------------------------------------------------- */

  /** Live `<img>` elements, keyed `z/x/y`, so panning reuses tiles instead of refetching them. */
  const tiles = new Map<string, HTMLImageElement>();

  const size = (): { width: number; height: number } => ({
    width: element.clientWidth || 320,
    height: element.clientHeight || 320,
  });

  const render = (): void => {
    const { width, height } = size();
    const centrePoint = project(centre, zoom);

    // The world pixel at the top-left corner of the viewport.
    const left = centrePoint.x * TILE_SIZE - width / 2;
    const top = centrePoint.y * TILE_SIZE - height / 2;

    const firstX = Math.floor(left / TILE_SIZE);
    const lastX = Math.floor((left + width) / TILE_SIZE);
    const firstY = Math.floor(top / TILE_SIZE);
    const lastY = Math.floor((top + height) / TILE_SIZE);

    const wanted = new Set<string>();

    for (let ty = firstY; ty <= lastY; ty++) {
      // There is no tile above the north pole or below the south one — leave those blank
      // rather than asking for something that cannot exist.
      if (ty < 0 || ty >= 2 ** zoom) continue;

      for (let tx = firstX; tx <= lastX; tx++) {
        // ...but the world does wrap east to west, so panning past the date line carries on.
        const wrappedX = wrapTileX(tx, zoom);
        const key = `${zoom}/${wrappedX}/${ty}@${tx}`;
        wanted.add(key);

        let img = tiles.get(key);
        if (!img) {
          img = el('img', {
            class: 'map__tile',
            src: `/api/tiles/${layer}/${zoom}/${wrappedX}/${ty}.png`,
            alt: '',
            draggable: 'false',
            decoding: 'async',
          }) as HTMLImageElement;
          // A tile that fails is left blank rather than showing a broken-image glyph.
          img.addEventListener('error', () => img?.classList.add('map__tile--missing'));
          img.addEventListener('load', () => img?.classList.add('map__tile--loaded'));
          tiles.set(key, img);
          tileLayer.append(img);
        }

        img.style.transform = `translate3d(${tx * TILE_SIZE - left}px, ${ty * TILE_SIZE - top}px, 0)`;
      }
    }

    for (const [key, img] of tiles) {
      if (wanted.has(key)) continue;
      img.remove();
      tiles.delete(key);
    }

    renderAccuracy();
  };

  const renderAccuracy = (): void => {
    if (
      !you ||
      accuracyMetres === null ||
      !Number.isFinite(accuracyMetres) ||
      accuracyMetres <= 0
    ) {
      accuracyDisc.hidden = true;
      return;
    }

    // Mercator stretches with latitude, so this has to be computed here rather than from a
    // constant — otherwise the circle would be honest only at the equator.
    const radiusPx = accuracyMetres / metresPerPixel(you.lat, zoom);

    // Where the *device* is, in screen pixels from the middle. The crosshair is the point being
    // chosen and moves with every drag; this disc has to stay where the person is standing.
    const here = project(you, zoom);
    const middle = project(centre, zoom);
    const offsetX = (here.x - middle.x) * TILE_SIZE;
    const offsetY = (here.y - middle.y) * TILE_SIZE;

    const { width, height } = size();
    // Hidden when it is meaninglessly small, absurdly large, or entirely off the screen —
    // a disc clamped to the edge would be a claim about where somebody is that is simply false.
    accuracyDisc.hidden =
      radiusPx < 6 ||
      radiusPx > Math.max(width, height) * 2 ||
      Math.abs(offsetX) - radiusPx > width / 2 ||
      Math.abs(offsetY) - radiusPx > height / 2;

    accuracyDisc.style.width = `${radiusPx * 2}px`;
    accuracyDisc.style.height = `${radiusPx * 2}px`;
    accuracyDisc.style.setProperty('--map-you-x', `${offsetX}px`);
    accuracyDisc.style.setProperty('--map-you-y', `${offsetY}px`);
  };

  const moved = (): void => {
    render();
    options.onMove?.(centre, zoom);
  };

  /** Shift the view by a number of screen pixels. */
  const panByPixels = (dx: number, dy: number): void => {
    const point = project(centre, zoom);
    centre = unproject({ x: point.x + dx / TILE_SIZE, y: point.y + dy / TILE_SIZE }, zoom);
    centre = { lat: clampLatitude(centre.lat), lng: centre.lng };
  };

  /**
   * Change zoom while keeping one screen point fixed.
   *
   * With `anchor` null the centre stays put, which is what the buttons want. With an anchor it
   * is the point under your fingers that stays put, which is what a pinch has to do or the map
   * squirms away from you.
   */
  const zoomTo = (next: number, anchor: { x: number; y: number } | null): void => {
    const target = clamp(Math.round(next), MIN_ZOOM, maxZoom);
    if (target === zoom) return;

    if (!anchor) {
      zoom = target;
      moved();
      return;
    }

    const { width, height } = size();
    const offsetX = anchor.x - width / 2;
    const offsetY = anchor.y - height / 2;

    // Put the anchor at the centre, change zoom, then put it back where it was.
    panByPixels(offsetX, offsetY);
    zoom = target;
    panByPixels(-offsetX, -offsetY);
    moved();
  };

  /* --- gestures ---------------------------------------------------------- */

  const pointers = new Map<number, { x: number; y: number }>();
  let dragging = false;
  let last: { x: number; y: number } | null = null;
  let pinchDistance = 0;
  let travelled = 0;
  let lastTapAt = 0;

  const localPoint = (event: PointerEvent): { x: number; y: number } => {
    const box = element.getBoundingClientRect();
    return { x: event.clientX - box.left, y: event.clientY - box.top };
  };

  const distanceBetween = (): number => {
    const [a, b] = [...pointers.values()];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const midpoint = (): { x: number; y: number } => {
    const [a, b] = [...pointers.values()];
    if (!a || !b) return { x: 0, y: 0 };
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (event.target === zoomIn || event.target === zoomOut) return;

    element.setPointerCapture(event.pointerId);
    pointers.set(event.pointerId, localPoint(event));

    if (pointers.size === 1) {
      dragging = true;
      travelled = 0;
      last = localPoint(event);
    } else if (pointers.size === 2) {
      dragging = false;
      pinchDistance = distanceBetween();
    }
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (!pointers.has(event.pointerId)) return;
    const point = localPoint(event);
    pointers.set(event.pointerId, point);

    if (pointers.size >= 2) {
      const spread = distanceBetween();
      if (pinchDistance > 0 && spread > 0) {
        // A doubling of the finger spread is exactly one zoom level, which is what makes a
        // pinch feel like it is scaling the world rather than nudging a slider.
        const levels = Math.log2(spread / pinchDistance);
        if (Math.abs(levels) >= 1) {
          zoomTo(zoom + Math.sign(levels), midpoint());
          pinchDistance = spread;
        }
      }
      return;
    }

    if (!dragging || !last) return;

    const dx = point.x - last.x;
    const dy = point.y - last.y;
    travelled += Math.hypot(dx, dy);
    last = point;

    panByPixels(-dx, -dy);
    moved();
  };

  const onPointerUp = (event: PointerEvent): void => {
    pointers.delete(event.pointerId);
    if (element.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId);

    if (pointers.size === 0) {
      // A tap that did not drag, twice in quick succession, means zoom in — the one gesture
      // every map on earth has taught people to expect.
      if (dragging && travelled < DRAG_SLOP_PX) {
        const now = Date.now();
        if (now - lastTapAt < 300) {
          zoomTo(zoom + 1, localPoint(event));
          lastTapAt = 0;
        } else {
          lastTapAt = now;
        }
      }
      dragging = false;
      last = null;
    }

    if (pointers.size < 2) pinchDistance = 0;
  };

  const onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    const box = element.getBoundingClientRect();
    zoomTo(zoom + (event.deltaY < 0 ? 1 : -1), {
      x: event.clientX - box.left,
      y: event.clientY - box.top,
    });
  };

  /** Arrow keys pan and +/- zoom, so this is usable without a pointing device at all. */
  const onKeyDown = (event: KeyboardEvent): void => {
    const step = event.shiftKey ? 160 : 48;
    const pans: Record<string, [number, number]> = {
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
    };

    const pan = pans[event.key];
    if (pan) {
      event.preventDefault();
      panByPixels(pan[0], pan[1]);
      moved();
      return;
    }

    if (event.key === '+' || event.key === '=') zoomTo(zoom + 1, null);
    if (event.key === '-' || event.key === '_') zoomTo(zoom - 1, null);
  };

  element.tabIndex = 0;
  element.addEventListener('pointerdown', onPointerDown);
  element.addEventListener('pointermove', onPointerMove);
  element.addEventListener('pointerup', onPointerUp);
  element.addEventListener('pointercancel', onPointerUp);
  element.addEventListener('wheel', onWheel, { passive: false });
  element.addEventListener('keydown', onKeyDown);

  zoomIn.addEventListener('click', () => zoomTo(zoom + 1, null));
  zoomOut.addEventListener('click', () => zoomTo(zoom - 1, null));

  // The viewport decides how many tiles are needed, and it is not known until layout runs.
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(() => render()) : null;
  observer?.observe(element);

  // One paint before layout so the map is never briefly blank, and one after.
  render();
  requestAnimationFrame(render);

  return {
    element,
    centre: () => centre,
    zoom: () => zoom,

    panTo: (position, nextZoom) => {
      centre = { lat: clampLatitude(position.lat), lng: position.lng };
      if (nextZoom !== undefined) zoom = clamp(Math.round(nextZoom), MIN_ZOOM, maxZoom);
      moved();
    },

    setYou: (position, metres) => {
      you = position;
      accuracyMetres = metres;
      renderAccuracy();
    },

    destroy: () => {
      observer?.disconnect();
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerup', onPointerUp);
      element.removeEventListener('pointercancel', onPointerUp);
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('keydown', onKeyDown);
      tiles.clear();
      element.remove();
    },
  };
}

/** Which tile layers this deployment actually has. Null when the map is switched off entirely. */
export async function fetchMapConfig(): Promise<{ layers: string[]; maxZoom: number } | null> {
  try {
    const response = await fetch('/api/tiles');
    if (!response.ok) return null;

    const body = (await response.json()) as { layers?: unknown; maxZoom?: unknown };
    const layers = Array.isArray(body.layers)
      ? body.layers.filter((l) => typeof l === 'string')
      : [];
    if (layers.length === 0) return null;

    return {
      layers: layers as string[],
      maxZoom: typeof body.maxZoom === 'number' ? body.maxZoom : 19,
    };
  } catch {
    // No map is a perfectly survivable state: every screen that offers one has a path without.
    return null;
  }
}
